/*
 * learn-watch.js - the Watch page (sections/learn-watch2.liquid).
 *
 * One player, a TikTok-style side panel, ONE chip row (All · playlists) that
 * filters the Up-next list and the grid, a grid view and cinema mode. Nothing
 * third-party loads until the first player mounts (YouTube IFrame API on
 * demand, TikTok player v1 iframe, Instagram on tap).
 *
 * ≤900px (21 Aug round 5): no side rail, no ▲▼ arrows. Like · Comment · Share
 * are appended into the bottom nav pill (`.lh-qn` from learn-bottom-nav) as
 * `.lh-qn__actions` - styled by `.lh-qn.has-actions …` in learn-watch.css so
 * the nav section and learn-hub.css stay untouched. Next/prev = swipe up/down.
 * The chip row moves under the player (player view) / above the grid (grid
 * view) on mobile and back under the title on desktop.
 *
 *   likes / saves   → localStorage learn:likes / learn:saves (shared with learn.js)
 *                     + optional POST <api>/like | /save when data-api is set
 *   sound / cinema  → localStorage learn:watch:sound / learn:watch:cinema
 *   URL             → ?v=<article id> via history.replaceState on every swap. P172 (2 Oct 2026): ids,
 *                     because handles are translated per language; a ?v=<handle> in any language still opens its video
 *   keys            → N/↓ next · P/↑ previous · L like · C cinema · Esc · Space (stage)
 *
 * TikTok player v1 speaks postMessage: {"x-tiktok-player":true,type,value}.
 *
 * Sound (22 Aug): the player is always mounted with `muted=0`. Measured on
 * staging: `muted=1` in the URL PINS the player muted (unMute via postMessage
 * flips onMute:false then the player re-mutes itself, and its volume slider
 * is disabled), while `muted=0` lets the player start muted by itself where
 * the browser blocks sound and then honours unMute. Our Sound toggle
 * ([data-sound]: a chip in the stage tools on desktop, the first item of the
 * bottom pill's action group on mobile) sends unMute/mute; the player's
 * onMute events drive the button state; the preference lives in
 * sessionStorage ("learn:watch:sound"). After the first gesture on the page
 * (state.active) every mount plays with sound unless the user turned it off.
 * onStateChange value 0 = ended, 1 playing, 2 paused, 3 buffering. Commands
 * play / pause / mute / unMute / seek. When no end event arrives we fall back
 * to onCurrentTime reaching duration, then to watch_seconds + 2 s.
 */
(function () {
  "use strict";
  var root = document.querySelector("[data-watch]");
  if (!root) return;

  var stkT = window.stkT || function (k, f) { return f; };
  function stkFill(s, vars) {
    return String(s).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    });
  }
  function stkRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return r.charAt(r.length - 1) === "/" ? r : r + "/";
  }

  var $ = function (sel, el) { return (el || root).querySelector(sel); };
  var $$ = function (sel, el) { return [].slice.call((el || root).querySelectorAll(sel)); };
  var LS = {
    get: function (k, d) { try { var v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };
  var SS = {
    get: function (k, d) { try { var v = sessionStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
    set: function (k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };

  /* ---------- data ---------- */
  var items = [];
  try { items = JSON.parse($("[data-watch-data]").textContent) || []; } catch (e) { items = []; }
  if (!items.length) return;
  var byHandle = {};
  // P172 (2 Oct 2026): handles are translated per language, so ?v= links carry the article id ("aid",
  // the same on every storefront) and this map resolves it back to this page's item.
  var byId = {};
  // One place for item normalisation: the initial parse AND every page appended by the
  // continuous loader below go through this, so an item from page 3 behaves like page 1.
  function prep(it, i) {
    it.index = i;
    it.tags = it.tags || [];
    it.family = famOf(it.platform);
    it.vertical = it.platform === "tiktok" || it.platform === "youtube_short" || it.platform === "instagram_reel";
    if (it.orient) { var o = String(it.orient).toLowerCase(); if (o === "portrait") it.vertical = true; else if (o === "landscape") it.vertical = false; }   // learn.orientation metafield wins
    it.tutorial = isTutorial(it);
    it.hashtags = hashtagsOf(it);
    byHandle[it.handle] = it;
    if (it.aid != null) byId[String(it.aid)] = it;   // P172
    return it;
  }
  items.forEach(prep);

  function famOf(p) { p = p || ""; if (p.indexOf("youtube") === 0) return "youtube"; if (p.indexOf("instagram") === 0) return "instagram"; return p; }
  function isTutorial(it) {
    if (it.ctype === "tutorial") return true;
    if (it.tags.some(function (t) { return /tutorial/i.test(t); })) return true;
    return /\btutorial|\btip\s*\d|^tip\b|\bhow to\b/i.test(it.title || "");
  }
  function hashtagsOf(it) {
    var seen = {}, out = [];
    function add(t, from) { t = String(t || "").replace(/^#/, "").trim(); if (!t) return; var k = t.toLowerCase(); if (seen[k]) return; seen[k] = 1; out.push({ tag: t, from: from }); }
    it.tags.forEach(function (t) { add(t, "tag"); });
    if (it.topic && it.topic.name) add(it.topic.name, "topic");
    ((it.title || "") + " " + (it.caption || "")).replace(/#([\p{L}\p{N}_]+)/gu, function (m, t) { add(t, "hash"); return m; });
    return out;
  }
  function cleanTitle(t) { return String(t || "").replace(/#[\p{L}\p{N}_]+/gu, "").replace(/\s{2,}/g, " ").trim(); }
  function fmtN(n) { n = Number(n) || 0; if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, "") + "M"; if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1).replace(/\.0$/, "") + "K"; return String(n); }
  function fmtT(s) { s = Math.round(Number(s) || 0); return Math.floor(s / 60) + ":" + ("0" + (s % 60)).slice(-2); }
  function platformLabel(p) { return { youtube: "YouTube", youtube_short: "YouTube", tiktok: "TikTok", instagram_reel: "Instagram", instagram_post: "Instagram" }[p] || (p || stkT("stk.learn_watch.platform_label_fallback", "Video")); }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }

  /* ---------- config + refs ---------- */
  var cfg = {
    autoplay: root.getAttribute("data-autoplay") === "true",
    nextSecs: parseInt(root.getAttribute("data-next-seconds"), 10) || 5,
    tab: root.getAttribute("data-default-tab") || "auto",
    api: (root.getAttribute("data-api") || "").replace(/\/$/, ""),
    shareBase: root.getAttribute("data-share-base") || (location.origin + location.pathname),
    initial: root.getAttribute("data-initial") || "",
    articleMode: root.getAttribute("data-article-mode") === "true",   // article.learn-video renders this section: URL stays the article's until the user moves on
    titleSuffix: root.getAttribute("data-title-suffix") || "",
    defaultView: root.getAttribute("data-default-view") || "player",
    rotate: root.getAttribute("data-rotate") !== "false"
  };
  var el = {
    stage: $("[data-stage]"), player: $("[data-player]"), box: $("[data-player-box]"), mount: $("[data-mount]"), poster: $("[data-poster]"),
    playBig: $("[data-play-big]"),
    nextOverlay: $("[data-next-overlay]"), nextCount: $("[data-next-count]"), nextThumb: $("[data-next-thumb]"), nextTitle: $("[data-next-title]"), nextMeta: $("[data-next-meta]"),
    endOverlay: $("[data-end-overlay]"),
    prev: $("[data-prev]"), next: $("[data-next]"), cinema: $("[data-cinema]"), backdrop: $("[data-cinema-backdrop]"),
    top: $(".lw-top"), topRight: $(".lw-top__right"), qhead: $("[data-qhead]"), gridHead: $("[data-grid-head]"),
    date: $("[data-date]"), badge: $("[data-platform-badge]"), duration: $("[data-duration]"), watchOn: $("[data-watch-on]"), watchOnLabel: $("[data-watch-on-label]"),
    title: $("[data-title]"), captionWrap: $("[data-caption-wrap]"), caption: $("[data-caption]"), captionMore: $("[data-caption-more]"), tags: $("[data-tags]"),
    transcript: $("[data-transcript]"), transcriptBody: $("[data-transcript-body]"),
    likes: $$("[data-like]"), likeCounts: $$("[data-like-count]"), saves: $$("[data-save]"), sounds: $$("[data-sound]"), saveCounts: $$("[data-save-count]"), commentCounts: $$("[data-comment-count]"), queueCount: $("[data-queue-count]"),
    glyph: $("[data-platform-glyph]"), share: $(".lw-share"), linkrow: $(".lw-linkrow"), commentsPane: $('[data-tab-pane="comments"]'), sheets: $$("[data-sheet]"), sheetBackdrop: $("[data-sheet-backdrop]"),
    copyUrl: $("[data-copy-url]"), copyBtn: $("[data-copy-link]"),
    commentsHost: $("[data-comments-host]"), formHost: $("[data-comment-form-host]"), panelScroll: $("[data-panel-scroll]"),
    queue: $("[data-queue]"), queueEmpty: $("[data-queue-empty]"), playlists: $("[data-playlists]"), grid: $("[data-grid]"),
    viewPlayer: $('[data-view="player"]'), viewGrid: $('[data-view="grid"]')
  };
  var baseTitle = document.title;
  var mqMobile = window.matchMedia("(max-width: 900px)");
  var isMobile = function () { return mqMobile.matches; };
  var GLYPH = {
    tiktok: '<svg viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>',
    youtube: '<svg viewBox="0 0 24 24"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.2 3.6-6.2 3.6Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24"><path d="M12 2.2c3.2 0 3.6 0 4.8.1 3.2.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 4 2.4 7.2 2.3c1.3-.1 1.6-.1 4.8-.1ZM12 0C8.7 0 8.3 0 7.1.1 2.7.3.3 2.7.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.2 4.4 2.6 6.8 7 7 1.2.1 1.6.1 4.9.1s3.7 0 4.9-.1c4.4-.2 6.8-2.6 7-7 .1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.2-4.4-2.6-6.8-7-7C15.7 0 15.3 0 12 0Zm0 5.8a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.4-11.8a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9Z"/></svg>'
  };
  // bold speaker glyph (2.25 stroke, round caps) - body fills when sound is on; waves (on) / small x (off) swap via [aria-pressed] in CSS
  var SOUND_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="lw-snd__body" d="M3.5 9.5H7l5.5-4.5v14L7 14.5H3.5z" stroke="currentColor" stroke-width="2.25" stroke-linejoin="round" fill="none"/><g class="lw-snd__on" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" fill="none"><path d="M16 9.5a3.5 3.5 0 0 1 0 5"/><path d="M18.75 6.75a7.25 7.25 0 0 1 0 10.5"/></g><g class="lw-snd__off" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" fill="none"><path d="M20 4.5 4.5 20"/></g></svg>';
  // sound: null = no preference yet (on once the page has a gesture), true/false = the user's choice this session
  var state = { cur: null, order: [], playlist: "all", sound: SS.get("learn:watch:sound", null), active: !!(navigator.userActivation && navigator.userActivation.hasBeenActive), kind: null, yt: null, tt: null, ttMutedParam: 0, ttSeek: 0, ttLastT: 0, ttEnded: false, ttTimer: null, countdown: null, playing: false, muted: true, view: "player" };
  function wantSound() { return state.sound !== false; }
  // first gesture anywhere on the page (toggle, Like, Next, swipe, a tap on the stage margin) - from then on mounts play with sound
  function markActive() { if (state.active) return; state.active = true; ["pointerdown", "keydown", "touchend"].forEach(function (ev) { document.removeEventListener(ev, markActive, true); }); }
  ["pointerdown", "keydown", "touchend"].forEach(function (ev) { document.addEventListener(ev, markActive, { capture: true, passive: true }); });

  /* ---------- toast ---------- */
  var toastEl = document.createElement("div"); toastEl.className = "lw-toast"; toastEl.setAttribute("role", "status"); document.body.appendChild(toastEl);
  var toastT;
  function toast(msg) { toastEl.textContent = msg; toastEl.classList.add("is-on"); clearTimeout(toastT); toastT = setTimeout(function () { toastEl.classList.remove("is-on"); }, 1800); }
  function copyText(t) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(t).catch(function () { return legacyCopy(t); });
    return Promise.resolve(legacyCopy(t));
  }
  function legacyCopy(t) { var ta = document.createElement("textarea"); ta.value = t; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); } catch (e) { /* no-op */ } document.body.removeChild(ta); }

  /* ---------- mobile: Like · Comment · Share live in the bottom nav pill ----------
     learn-bottom-nav renders `.lh-qn` (Home · Topics · Watch · Me, --qn-i slider).
     On the watch page we append a divider + `.lh-qn__actions` at runtime; the
     slider keeps travelling over the 4 nav items only (it is a sibling, same
     --qn-i). Styled by `.lh-qn.has-actions …` in learn-watch.css. The nav is
     display:none above 900px, so desktop keeps its actions under the player. */
  var qn = document.querySelector("[data-lh-qn]");
  if (qn && !qn.querySelector(".lh-qn__actions")) {
    var ICO = {
      like: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" stroke="currentColor" stroke-width="2.25" stroke-linejoin="round" fill="none"/></svg>',
      comment: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4V5Z" stroke="currentColor" stroke-width="2.25" stroke-linejoin="round" fill="none"/></svg>',
      share: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15V3m0 0L7.5 7.5M12 3l4.5 4.5M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>',
      sound: SOUND_SVG
    };
    var div = document.createElement("span"); div.className = "lh-qn__div"; div.setAttribute("aria-hidden", "true");
    var grp = document.createElement("div"); grp.className = "lh-qn__actions"; grp.setAttribute("role", "group"); grp.setAttribute("aria-label", stkT("stk.learn_watch.aria_video_actions", "Video actions"));
    // The Like button exists only for a signed-in customer (MY-LEARNING-SPEC.md,
    // answer 5); the config marker carries data-customer when there is one.
    var signedIn = !!document.querySelector("[data-learn-config][data-customer]");
    grp.innerHTML =
      '<button type="button" class="lh-qn__item lh-qn__act" data-sound aria-pressed="false" aria-label="' + esc(stkT("stk.learn_watch.sound_off", "Sound off")) + '" title="' + esc(stkT("stk.learn_watch.action_title_sound", "Sound")) + '">' + ICO.sound + '</button>' +
      (signedIn ? '<button type="button" class="lh-qn__item lh-qn__act" data-like aria-pressed="false" aria-label="' + esc(stkT("stk.learn_watch.action_like", "Like")) + '" title="' + esc(stkT("stk.learn_watch.action_like", "Like")) + '">' + ICO.like + '</button>' : '') +
      '<button type="button" class="lh-qn__item lh-qn__act" data-rail-comments aria-label="' + esc(stkT("stk.learn_watch.action_comments", "Comments")) + '" title="' + esc(stkT("stk.learn_watch.action_comments", "Comments")) + '">' + ICO.comment + '</button>' +
      '<button type="button" class="lh-qn__item lh-qn__act" data-rail-share aria-label="' + esc(stkT("stk.learn_watch.action_share", "Share")) + '" title="' + esc(stkT("stk.learn_watch.action_share", "Share")) + '">' + ICO.share + '</button>';
    qn.appendChild(div); qn.appendChild(grp); qn.classList.add("has-actions");
    // join the shared like / count collections so renderPanel + toggleLike keep them in sync
    $$("[data-like]", grp).forEach(function (b) { el.likes.push(b); });
    $$("[data-sound]", grp).forEach(function (b) { el.sounds.push(b); });
  }
  function tidyCounts() { $$("[data-hide-zero]", document).forEach(function (c) { var t = (c.textContent || "").trim(); c.hidden = !t || t === "0"; }); }

  /* ---------- ordering: the one chip row (All · playlists) ---------- */
  function playlistTest(btn) {
    var match = btn.getAttribute("data-match"), val = (btn.getAttribute("data-value") || "").toLowerCase();
    // P172 (2 Oct 2026): off-English the title and caption are translated, so the keyword picked a different
    // set per language (Printing 118 EN, 50 FR). There the snippet renders data-vids: the learn.video_id of
    // every video the keyword matches on EN. Tags are never translated, so a tag match still counts.
    // English renders no data-vids, so vids stays null and the original text match below runs unchanged.
    var vids = null, vl = (btn.getAttribute("data-vids") || "").split(/\s+/).filter(Boolean);
    if (vl.length) { vids = {}; vl.forEach(function (v) { vids[v] = 1; }); }
    return function (it) {
      switch (match) {
        case "tutorial": return it.tutorial;
        case "tag": return it.tags.some(function (t) { return t.toLowerCase() === val || t.toLowerCase().replace(/\s+/g, "-") === val; });
        case "topic": return !!(it.topic && (it.topic.handle || "").toLowerCase() === val);
        case "keyword":
          if (vids) return !!val && (vids[String(it.id)] === 1 || it.tags.join(" ").toLowerCase().indexOf(val) > -1);   // P172: off-English
          return val && ((it.title || "") + " " + (it.caption || "") + " " + it.tags.join(" ")).toLowerCase().indexOf(val) > -1;
        case "platform": return val && (it.family === val || it.platform === val);
        default: return true;
      }
    };
  }
  var playlistFns = { all: function () { return true; } };
  $$("[data-playlist]").forEach(function (btn) {
    var id = btn.getAttribute("data-playlist");
    if (id !== "all") playlistFns[id] = playlistTest(btn);
    var matches = items.filter(playlistFns[id]);
    var n = matches.length;
    var c = $("[data-count]", btn); if (c) c.textContent = n;
    var cover = $("[data-pl-cover]", btn);
    var withImg = matches.filter(function (it) { return it.thumb; })[0];
    if (cover && withImg) cover.src = withImg.thumb;
    btn.hidden = n === 0;
    btn.addEventListener("click", function () { setPlaylist(id); });
  });
  function recompute() {
    var fn = playlistFns[state.playlist] || playlistFns.all;
    state.order = items.filter(fn).map(function (it) { return it.handle; });
    var inOrder = {}; state.order.forEach(function (h) { inOrder[h] = 1; });
    $$("[data-watch-tile]").forEach(function (t) { t.hidden = !inOrder[t.getAttribute("data-watch-tile")]; });
    if (el.queueEmpty) el.queueEmpty.hidden = state.order.length > 0;
    if (el.queueCount) el.queueCount.textContent = state.order.length;
    $$("[data-playlist]").forEach(function (b) { var on = b.getAttribute("data-playlist") === state.playlist; b.classList.toggle("is-on", on); b.setAttribute("aria-pressed", on ? "true" : "false"); });
    updateArrows();
  }
  function setPlaylist(id) { state.playlist = id; recompute(); if (state.view === "player" && state.order.length && state.order.indexOf(state.cur) === -1) select(state.order[0], { autoplay: true }); }
  function curIdx() { return state.order.indexOf(state.cur); }
  function neighbour(d) { var i = curIdx(); if (i === -1) return state.order[0] || null; var j = i + d; return j >= 0 && j < state.order.length ? state.order[j] : null; }
  function updateArrows() {
    if (el.prev) el.prev.disabled = !neighbour(-1);
    if (el.next) { el.next.disabled = !neighbour(1); el.next.hidden = state.order.length < 2; }
    // within 5 of the end of what is loaded: ask the loader for the next page now, so
    // auto-advance never hits "That's everything here" while more pages exist
    var i = curIdx();
    if (i > -1 && state.order.length - i <= 5) root.dispatchEvent(new CustomEvent("lw:near-end"));
  }

  /* ---------- panel ---------- */
  function renderPanel(it) {
    var liked = inStore("learn:likes", it), saved = inStore("learn:saves", it);   // P172: by article id, handle as fallback
    if (el.title) el.title.textContent = cleanTitle(it.title) || it.title;
    if (el.caption) { el.caption.textContent = it.caption || ""; el.captionWrap.classList.add("is-clamped"); el.captionMore.hidden = true; el.captionMore.setAttribute("aria-expanded", "false"); el.captionMore.textContent = stkT("stk.learn_watch.caption_more", "more"); requestAnimationFrame(function () { el.captionMore.hidden = !(el.caption.scrollHeight > el.caption.clientHeight + 2); }); }
    if (el.tags) el.tags.innerHTML = it.hashtags.map(function (h) { return '<a class="lw-tag" href="' + stkRoot() + "search?type=article&q=" + encodeURIComponent(h.tag) + '"><b>#</b>' + esc(h.tag.replace(/\s+/g, "")) + "</a>"; }).join("");
    // transcript: real on-page text, not just a schema property. Collapsed by default so it
    // never pushes the actions below the fold; hidden entirely when the video has no speech.
    if (el.transcript) {
      var tx = it.transcript || "";
      el.transcript.hidden = !tx;
      el.transcript.open = false;
      if (el.transcriptBody) el.transcriptBody.textContent = tx;
    }
    if (el.date) { el.date.textContent = it.date || ""; el.date.setAttribute("datetime", it.dateISO || ""); }
    if (el.badge) { el.badge.textContent = platformLabel(it.platform); el.badge.className = "lw-badge lw-badge--" + it.family; }
    if (el.glyph) { el.glyph.innerHTML = GLYPH[it.family] || ""; el.glyph.setAttribute("title", platformLabel(it.platform)); }
    if (el.duration) { el.duration.hidden = !(it.secs > 0); el.duration.textContent = it.secs > 0 ? fmtT(it.secs) : ""; }
    if (el.watchOn) { el.watchOn.href = it.vurl || it.url; el.watchOnLabel.textContent = it.vurl ? stkFill(stkT("stk.learn_watch.watch_on", "Watch on {platform}"), { platform: platformLabel(it.platform) }) : stkT("stk.learn_watch.open_post", "Open post"); }
    el.likes.forEach(function (b) { setPressed(b, liked); }); el.saves.forEach(function (b) { setPressed(b, saved); });
    el.likeCounts.forEach(function (c) { c.textContent = fmtN((it.likes || 0) + (liked ? 1 : 0)); });
    el.saveCounts.forEach(function (c) { c.textContent = fmtN((it.saves || 0) + (saved ? 1 : 0)); });
    el.commentCounts.forEach(function (c) { c.textContent = it.comments || 0; });
    tidyCounts();
    // learn-me.js listens: public like/save counts come from the account API, and
    // a like/save/playlist click needs the item on screen for its snapshot.
    window.LearnWatchCurrent = function () { return { handle: it.handle, aid: it.aid != null ? String(it.aid) : "", title: cleanTitle(it.title) || it.title, share: shareUrl(it), image: it.thumb || it.poster || it.image || "" }; };
    try { document.dispatchEvent(new CustomEvent("lw:panel", { detail: { handle: it.handle, aid: it.aid != null ? String(it.aid) : "" } })); } catch (e) {}
    if (el.copyUrl) el.copyUrl.value = shareUrl(it);
    var ct = document.querySelector('template[data-watch-comments="' + it.handle + '"]'), cf = document.querySelector('template[data-watch-cform="' + it.handle + '"]');
    if (el.commentsHost) { el.commentsHost.innerHTML = ""; if (ct) el.commentsHost.appendChild(ct.content.cloneNode(true)); }
    if (el.formHost) { el.formHost.innerHTML = ""; if (cf) { el.formHost.appendChild(cf.content.cloneNode(true)); wireForm(); } }
    $$("[data-watch-tile]").forEach(function (t) { var on = t.getAttribute("data-watch-tile") === it.handle; t.classList.toggle("is-current", on); var a = $("[data-tile-link]", t); if (a) { if (on) a.setAttribute("aria-current", "true"); else a.removeAttribute("aria-current"); } });
  }
  function setPressed(btn, on) { if (btn) btn.setAttribute("aria-pressed", on ? "true" : "false"); }
  // P172 (2 Oct 2026): share, copy and address-bar links carry the article id, which opens the same video in every
  // language; handles are translated per language. The handle is the fallback for an item without one.
  function vKey(it) { return it && it.aid != null ? String(it.aid) : (it ? it.handle : ""); }
  function shareUrl(it) { return cfg.shareBase + "?v=" + encodeURIComponent(vKey(it)); }
  function wireForm() {
    var f = $(".lw-cform", el.formHost); if (!f) return;
    f.classList.add("is-collapsed");
    var ta = $("textarea", f);
    function grow() { ta.style.height = "auto"; ta.style.height = Math.min(140, ta.scrollHeight) + "px"; }
    // Shopify needs an email on every comment; visitors only give a username, so the hidden email becomes <username>@comments.stickiply.com
    var user = $("[data-cform-user]", f), mail = $("[data-cform-email]", f);
    if (user && mail) {
      var fill = function () { if (mail.value && mail.value.indexOf("@comments.") === -1) return; var h = (user.value || "").trim().replace(/^@+/, "").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^[-.]+|[-.]+$/g, "").slice(0, 40) || "guest"; mail.value = h + "@comments.stickiply.com"; };
      user.addEventListener("input", fill); f.addEventListener("submit", fill); fill();
    }
    ta.addEventListener("focus", function () { f.classList.remove("is-collapsed"); });
    ta.addEventListener("input", grow);
  }
  if (el.captionMore) el.captionMore.addEventListener("click", function () { var open = el.captionWrap.classList.toggle("is-clamped") === false; el.captionMore.setAttribute("aria-expanded", open ? "true" : "false"); el.captionMore.textContent = open ? stkT("stk.learn_watch.caption_less", "less") : stkT("stk.learn_watch.caption_more", "more"); });

  /* ---------- tabs ---------- */
  function setTab(name, opts) {
    $$("[data-tab-btn]").forEach(function (b) { var on = b.getAttribute("data-tab-btn") === name; b.setAttribute("aria-selected", on ? "true" : "false"); b.tabIndex = on ? 0 : -1; });
    $$("[data-tab-pane]").forEach(function (p) { p.hidden = !isMobile() && p.getAttribute("data-tab-pane") !== name; });   // mobile: both panes live (videos inline, comments in the sheet)
    if (el.formHost) el.formHost.hidden = !isMobile() && name !== "comments";   // desktop: the Add-comment box only belongs to the Comments tab
    if (opts && opts.scroll && el.panelScroll) { var pane = $('[data-tab-pane="' + name + '"]'); if (pane) { var top = pane.offsetTop - 60; if (window.innerWidth <= 900) { window.scrollTo({ top: pane.getBoundingClientRect().top + window.pageYOffset - 80, behavior: "smooth" }); } else { el.panelScroll.scrollTo({ top: top, behavior: "smooth" }); } } }
  }
  $$("[data-tab-btn]").forEach(function (b) { b.addEventListener("click", function () { setTab(b.getAttribute("data-tab-btn")); }); });
  var jump = $("[data-comments-jump]"); if (jump) jump.addEventListener("click", function () { if (isMobile()) { openSheet("comments"); return; } setTab("comments", { scroll: true }); var ta = $("textarea", el.formHost); if (ta) ta.focus({ preventScroll: true }); });

  /* ---------- player ---------- */
  function clearPlayer() {
    stopCountdown();
    if (state.yt && state.yt.destroy) { try { state.yt.destroy(); } catch (e) { /* already gone */ } }
    state.yt = null; state.tt = null; state.kind = null; state.playing = false; state.ttEnded = false;
    clearTimeout(state.ttTimer); state.ttTimer = null;
    el.mount.innerHTML = "";
    el.player.classList.remove("is-mounted");
    el.endOverlay.hidden = true;
  }
  function setPoster(it) {
    if (!el.poster) return;
    if (it.image) { el.poster.src = it.image; el.poster.hidden = false; } else { el.poster.removeAttribute("src"); el.poster.hidden = true; }
    el.player.classList.toggle("is-vertical", !!it.vertical);
    el.player.classList.toggle("is-landscape", !it.vertical);
    root.classList.toggle("is-portrait", !!it.vertical); root.classList.toggle("is-landscape", !it.vertical);   // card beside (portrait) or under (landscape) the player
  }
  function mount(it, autoplay, opts) {
    clearPlayer();
    var can = it.embeddable !== false && it.id;
    state.muted = !(wantSound() && state.active); renderSound();   // optimistic; the player's mute events confirm
    if (!can) { el.playBig.onclick = function () { if (it.vurl) window.open(it.vurl, "_blank", "noopener"); }; return; }
    el.playBig.onclick = function () { mount(it, true); };
    if (!autoplay) return;  // facade until the user presses play
    el.player.classList.add("is-mounted");
    if (it.family === "youtube") mountYouTube(it);
    else if (it.platform === "tiktok") mountTikTok(it, opts);
    else mountInstagram(it);
    document.dispatchEvent(new CustomEvent("learn:video-play", { detail: { platform: it.platform, id: it.id } }));
  }
  // No "Tap to unmute" pill of ours (Thomas, 21 Aug): the video autoplays - muted where the browser
  // requires it. Sound = our [data-sound] toggle (desktop stage tools + mobile pill), never an overlay.
  function renderSound() {
    var on = !state.muted;
    el.sounds.forEach(function (b) { b.setAttribute("aria-pressed", on ? "true" : "false"); b.setAttribute("aria-label", on ? stkT("stk.learn_watch.sound_on", "Sound on") : stkT("stk.learn_watch.sound_off", "Sound off")); b.title = on ? stkT("stk.learn_watch.sound_on_title", "Sound on (M)") : stkT("stk.learn_watch.sound_off_title", "Sound off (M)"); var t = b.querySelector("[data-sound-label]"); if (t) t.textContent = on ? stkT("stk.learn_watch.sound_on", "Sound on") : stkT("stk.learn_watch.sound_off", "Sound off"); });
  }
  function setSound(on) {
    state.sound = on; SS.set("learn:watch:sound", on); state.active = true;
    var it = byHandle[state.cur];
    if (state.kind === "tt" && state.tt) {
      if (on && state.ttMutedParam === 1 && it) { mount(it, true, { seek: state.ttLastT }); return; }   // a pinned (muted=1) mount can only be unmuted by remounting
      ttSend(on ? "unMute" : "mute");
    } else if (state.kind === "yt" && state.yt) { try { if (on) state.yt.unMute(); else state.yt.mute(); } catch (e) { /* ignore */ } }
    state.muted = !on; renderSound();
    if (el.sounds.length) el.sounds.forEach(pop);
  }
  el.sounds.forEach(function (b) { b.addEventListener("click", function () { setSound(state.muted); }); });

  // --- YouTube (IFrame API, nocookie host, loaded once on first use)
  var ytReady = null;
  function loadYT() {
    if (window.YT && window.YT.Player) return Promise.resolve();
    if (ytReady) return ytReady;
    ytReady = new Promise(function (resolve) {
      var prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () { if (prev) { try { prev(); } catch (e) { /* other script */ } } resolve(); };
      var s = document.createElement("script"); s.src = "https://www.youtube.com/iframe_api"; s.async = true; document.head.appendChild(s);
    });
    return ytReady;
  }
  function mountYouTube(it) {
    var host = document.createElement("div"); el.mount.appendChild(host);
    var token = it.handle;
    loadYT().then(function () {
      if (state.cur !== token || !el.player.classList.contains("is-mounted")) return;
      state.kind = "yt";
      state.yt = new YT.Player(host, {
        videoId: it.id, host: "https://www.youtube-nocookie.com",
        playerVars: { autoplay: 1, mute: (wantSound() && state.active) ? 0 : 1, playsinline: 1, rel: 0, modestbranding: 1, enablejsapi: 1, origin: location.origin },
        events: {
          onReady: function (e) { if (wantSound() && state.active) { try { e.target.unMute(); } catch (x) { /* ignore */ } } try { e.target.playVideo(); } catch (x) { /* ignore */ } var d = e.target.getDuration && e.target.getDuration(); if (d > 0 && el.duration) { el.duration.hidden = false; el.duration.textContent = fmtT(d); } },
          onStateChange: function (e) { state.playing = e.data === 1; if (e.data === 1 && state.yt && state.yt.isMuted) { try { state.muted = !!state.yt.isMuted(); renderSound(); } catch (x) { /* ignore */ } } if (e.data === 0) onEnded(); }
        }
      });
    });
  }

  // --- TikTok player v1
  function ttSend(type, value) { if (!state.tt || !state.tt.contentWindow) return; var msg = { "x-tiktok-player": true, type: type }; if (value !== undefined) msg.value = value; try { state.tt.contentWindow.postMessage(msg, "https://www.tiktok.com"); } catch (e) { /* ignore */ } }
  // always muted=0 (muted=1 pins the player muted - see the header note); the player starts muted by itself when the browser blocks sound
  function ttMutedParam() { return 0; }
  function mountTikTok(it, opts) {
    var f = document.createElement("iframe");
    state.ttMutedParam = ttMutedParam(); state.ttSeek = (opts && opts.seek > 1) ? Math.floor(opts.seek) : 0; state.ttLastT = 0;
    f.src = "https://www.tiktok.com/player/v1/" + encodeURIComponent(it.id) + "?autoplay=1&muted=" + state.ttMutedParam + "&loop=0&description=0&music_info=0&rel=0&controls=1&progress_bar=1&play_button=1&volume_control=1&fullscreen_button=1&timestamp=1";
    f.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen");
    f.setAttribute("allowfullscreen", ""); f.setAttribute("title", it.title || stkT("stk.learn_watch.tiktok_iframe_title_default", "TikTok video")); f.setAttribute("loading", "eager");
    el.mount.appendChild(f);
    state.kind = "tt"; state.tt = f;
    // fallback when the player never reports an end
    if (it.secs > 0) state.ttTimer = setTimeout(function () { if (state.kind === "tt" && !state.ttEnded) onEnded(); }, (it.secs + 2) * 1000);
  }
  window.addEventListener("message", function (e) {
    if (!state.tt || state.kind !== "tt") return;
    if (typeof e.origin !== "string" || e.origin.indexOf("tiktok.com") === -1) return;
    var d = e.data; if (!d || typeof d !== "object" || !d["x-tiktok-player"]) return;
    var it = byHandle[state.cur];
    if (d.type === "onPlayerReady") {
      if (state.ttSeek > 0) ttSend("seekTo", state.ttSeek);
      if (!wantSound()) ttSend("mute"); else if (state.active) ttSend("unMute");   // never unMute without a gesture: a blocked unmute pauses the video
      ttSend("play");
    }
    if (d.type === "onStateChange") { var v = Number(d.value); state.playing = v === 1; if (v === 0 && !state.ttEnded) { state.ttEnded = true; onEnded(); } }
    if (d.type === "onMute") {
      state.muted = !!(d.value && typeof d.value === "object" && d.value.muted !== undefined ? d.value.muted : d.value); renderSound();
      // once playing, a change that differs from our preference came from the player's own button: remember it
      if (state.playing && state.active && !state.muted !== wantSound()) { state.sound = !state.muted; SS.set("learn:watch:sound", state.sound); }
    }
    if (d.type === "onCurrentTime" && d.value) {
      var cur = Number(d.value.currentTime), dur = Number(d.value.duration);
      if (cur > 0) state.ttLastT = cur;
      if (dur > 0 && it && el.duration && el.duration.hidden) { el.duration.hidden = false; el.duration.textContent = fmtT(dur); }
      if (dur > 0 && cur >= dur - 0.35 && !state.ttEnded) { state.ttEnded = true; onEnded(); }
    }
  });

  // --- Instagram: embed on tap only (no autoplay, no end event)
  function mountInstagram(it) {
    var f = document.createElement("iframe");
    f.src = "https://www.instagram.com/" + (it.platform === "instagram_post" ? "p" : "reel") + "/" + encodeURIComponent(it.id) + "/embed";
    f.setAttribute("allow", "autoplay; clipboard-write; encrypted-media; picture-in-picture"); f.setAttribute("allowfullscreen", ""); f.setAttribute("title", it.title || stkT("stk.learn_watch.instagram_iframe_title_default", "Instagram video")); f.setAttribute("loading", "eager");
    el.mount.appendChild(f); state.kind = "ig";
    if (it.secs > 0) state.ttTimer = setTimeout(function () { if (state.kind === "ig") onEnded(); }, (it.secs + 2) * 1000);
  }

  function togglePlay() {
    if (!el.player.classList.contains("is-mounted")) { var it = byHandle[state.cur]; if (it) mount(it, true); return; }
    if (state.kind === "yt" && state.yt) { try { if (state.yt.getPlayerState() === 1) state.yt.pauseVideo(); else state.yt.playVideo(); } catch (e) { /* ignore */ } }
    else if (state.kind === "tt") ttSend(state.playing ? "pause" : "play");
  }

  /* ---------- auto-next ---------- */
  function onEnded() {
    if (document.hidden) { state.playing = false; }
    var nh = neighbour(1);
    if (!nh) { el.endOverlay.hidden = false; return; }
    startCountdown(nh);
  }
  function startCountdown(nh) {
    stopCountdown();
    var n = byHandle[nh]; if (!n) return;
    el.nextThumb.src = n.thumb || n.image || ""; el.nextThumb.hidden = !(n.thumb || n.image);
    el.nextTitle.textContent = cleanTitle(n.title) || n.title; el.nextMeta.textContent = platformLabel(n.platform) + " · " + (n.date || "");
    var left = cfg.nextSecs; el.nextCount.textContent = left; el.nextOverlay.hidden = false;
    state.countdown = setInterval(function () { left -= 1; el.nextCount.textContent = Math.max(0, left); if (left <= 0) { stopCountdown(); select(nh, { autoplay: true }); } }, 1000);
  }
  function stopCountdown() { clearInterval(state.countdown); state.countdown = null; if (el.nextOverlay) el.nextOverlay.hidden = true; }
  $("[data-next-cancel]").addEventListener("click", function () { stopCountdown(); });
  $("[data-next-now]").addEventListener("click", function () { var nh = neighbour(1); stopCountdown(); if (nh) select(nh, { autoplay: true }); });
  $("[data-end-replay]").addEventListener("click", function () { var it = byHandle[state.cur]; el.endOverlay.hidden = true; if (it) mount(it, true); });
  $("[data-end-all]").addEventListener("click", function () { el.endOverlay.hidden = true; state.playlist = "all"; recompute(); if (state.order[0]) select(state.order[0], { autoplay: true }); });

  /* ---------- selection ---------- */
  function select(handle, opts) {
    opts = opts || {};
    var it = byHandle[handle]; if (!it) return;
    var changed = state.cur !== handle;
    state.cur = handle;
    setPoster(it); renderPanel(it); updateArrows();
    var auto = opts.autoplay !== undefined ? opts.autoplay : cfg.autoplay;
    if (it.family === "instagram" && !opts.user) auto = false;   // IG embeds don't autoplay - keep the facade
    mount(it, auto);
    // URL: /pages/watch?v=handle. On article.learn-video the address stays the article's until the viewer moves to another video.
    if (booted || !cfg.articleMode) { try { var u = new URL(cfg.articleMode ? cfg.shareBase : location.href, location.origin); if (!cfg.articleMode) u.search = location.search; u.searchParams.set("v", vKey(it));   /* P172: id, not the per-language handle */ history.replaceState(null, "", u.pathname + u.search + (location.hash || "")); } catch (e) { /* ignore */ } }
    document.title = (cleanTitle(it.title) || it.title) + " · " + (cfg.titleSuffix || baseTitle.replace(/^.*?·\s*/, ""));
    if (changed && opts.user) {
      var tile = el.queue && $('[data-watch-tile="' + handle + '"]', el.queue);
      if (tile && tile.scrollIntoView && !tile.hidden) tile.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
    document.dispatchEvent(new CustomEvent("learn:watch-select", { detail: { handle: handle } }));
  }
  function go(d, user) { var h = neighbour(d); if (h) select(h, { autoplay: true, user: user }); }
  if (el.prev) el.prev.addEventListener("click", function () { go(-1, true); });
  if (el.next) el.next.addEventListener("click", function () { go(1, true); });

  // tiles (queue + grid) - swap in place, keep the href for new-tab/middle-click
  root.addEventListener("click", function (e) {
    var a = e.target.closest && e.target.closest("[data-tile-link]");
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    var h = a.getAttribute("data-tile-link");
    if (state.view === "grid") setView("player");
    select(h, { autoplay: true, user: true });
    if (window.innerWidth <= 900 || state.view === "grid") el.stage.scrollIntoView({ block: "start", behavior: "smooth" });
  });

  /* ---------- like / save ---------- */
  // P172 (2 Oct 2026): the device cache is keyed by article id ("aid", the same in every language); a handle
  // stored before the switch still counts as the same item and is replaced by the id on the next click.
  function itemKeys(it) { return [it.aid != null ? String(it.aid) : "", it.handle].filter(Boolean); }
  function inStore(key, it) { var cur = LS.get(key, []); return itemKeys(it).some(function (k) { return cur.indexOf(k) > -1; }); }
  function toggleStore(key, it) { var keys = itemKeys(it), was = inStore(key, it); var cur = LS.get(key, []).filter(function (k) { return keys.indexOf(k) < 0; }); if (!was) cur.unshift(keys[0]); LS.set(key, cur); return !was; }
  function postApi(action, it, on) {
    if (window.LearnMe || !cfg.api || !window.fetch) return;   // learn-me.js owns the POST when present
    fetch(cfg.api + "/" + action, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ aid: it.aid != null ? String(it.aid) : "", handle: it.handle, on: on }) }).catch(function () { /* offline / not deployed yet */ });
  }
  function pop(btn) { btn.classList.remove("is-pop"); void btn.offsetWidth; btn.classList.add("is-pop"); }
  function toggleLike() { var it = byHandle[state.cur]; if (!it) return; var on = toggleStore("learn:likes", it); el.likes.forEach(function (b) { setPressed(b, on); pop(b); }); el.likeCounts.forEach(function (c) { c.textContent = fmtN((it.likes || 0) + (on ? 1 : 0)); }); tidyCounts(); postApi("like", it, on); }
  function toggleSave() { var it = byHandle[state.cur]; if (!it) return; var on = toggleStore("learn:saves", it); el.saves.forEach(function (b) { setPressed(b, on); pop(b); }); el.saveCounts.forEach(function (c) { c.textContent = fmtN((it.saves || 0) + (on ? 1 : 0)); }); tidyCounts(); postApi("save", it, on); toast(on ? stkT("stk.learn_watch.toast_saved_to_my_learning", "Saved to My learning") : stkT("stk.learn_watch.toast_removed_from_saved", "Removed from saved")); }
  el.likes.forEach(function (b) { b.addEventListener("click", toggleLike); });
  el.saves.forEach(function (b) { b.addEventListener("click", toggleSave); });

  /* ---------- share / copy / embed ---------- */
  function embedCode(it) {
    if (it.family === "youtube") return '<iframe width="560" height="' + (it.vertical ? 996 : 315) + '" src="https://www.youtube-nocookie.com/embed/' + it.id + '" title="' + esc(it.title) + '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    if (it.platform === "tiktok") return '<blockquote class="tiktok-embed" cite="' + esc(it.vurl || "") + '" data-video-id="' + it.id + '" style="max-width:605px;min-width:325px"><section></section></blockquote><script async src="https://www.tiktok.com/embed.js"></' + 'script>';
    if (it.family === "instagram") return '<blockquote class="instagram-media" data-instgrm-permalink="' + esc(it.vurl || "") + '" data-instgrm-version="14"></blockquote><script async src="https://www.instagram.com/embed.js"></' + 'script>';
    return shareUrl(it);
  }
  $$("[data-share]").forEach(function (b) {
    b.addEventListener("click", function () {
      var it = byHandle[state.cur]; if (!it) return;
      var url = shareUrl(it), text = cleanTitle(it.title) || it.title, kind = b.getAttribute("data-share"), href = null;
      switch (kind) {
        case "whatsapp": href = "https://wa.me/?text=" + encodeURIComponent(text + " " + url); break;
        case "facebook": href = "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url); break;
        case "x": href = "https://twitter.com/intent/tweet?text=" + encodeURIComponent(text) + "&url=" + encodeURIComponent(url); break;
        case "pinterest": href = "https://pinterest.com/pin/create/button/?url=" + encodeURIComponent(url) + "&description=" + encodeURIComponent(text) + (it.image ? "&media=" + encodeURIComponent(it.image) : ""); break;
        case "embed": copyText(embedCode(it)).then(function () { b.classList.add("is-done"); setTimeout(function () { b.classList.remove("is-done"); }, 1500); toast(stkT("stk.learn_watch.toast_embed_copied", "Embed code copied")); }); return;
        case "native":
          if (navigator.share) { navigator.share({ title: text, url: url }).catch(function () { /* dismissed */ }); }
          else copyText(url).then(function () { toast(stkT("stk.learn_watch.toast_link_copied", "Link copied")); });
          return;
      }
      if (href) window.open(href, "_blank", "noopener,width=640,height=560");
    });
  });
  function copyLink() { var it = byHandle[state.cur]; if (!it) return; copyText(shareUrl(it)).then(function () { el.copyBtn.classList.add("is-done"); el.copyBtn.textContent = stkT("stk.learn_watch.copy_link_copied", "Copied"); setTimeout(function () { el.copyBtn.classList.remove("is-done"); el.copyBtn.textContent = stkT("stk.learn_watch.copy_link_label", "Copy link"); }, 1500); toast(stkT("stk.learn_watch.toast_link_copied", "Link copied")); }); }
  el.copyBtn.addEventListener("click", copyLink);
  if (el.copyUrl) el.copyUrl.addEventListener("focus", function () { this.select(); });

  /* ---------- view: player | grid ---------- */
  function setView(v) {
    state.view = v;
    el.viewPlayer.hidden = v !== "player"; el.viewGrid.hidden = v !== "grid";
    $$("[data-view-btn]").forEach(function (b) { var on = b.getAttribute("data-view-btn") === v; b.classList.toggle("is-on", on); b.setAttribute("aria-pressed", on ? "true" : "false"); });
    placePlaylists();
    if (v === "player") requestAnimationFrame(measureStage);
    if (v === "grid") { if (state.kind === "yt" && state.yt) { try { state.yt.pauseVideo(); } catch (e) { /* ignore */ } } if (state.kind === "tt") ttSend("pause"); stopCountdown(); }
    else if (state.cur && el.player.classList.contains("is-mounted")) { if (state.kind === "yt" && state.yt) { try { state.yt.playVideo(); } catch (e) { /* ignore */ } } if (state.kind === "tt") ttSend("play"); }
  }
  $$("[data-view-btn]").forEach(function (b) { b.addEventListener("click", function () { setView(b.getAttribute("data-view-btn")); }); });
  $$("[data-views]").forEach(function (v) { v.textContent = fmtN(v.getAttribute("data-views")); });

  /* ---------- cinema ---------- */
  function setCinema(on, persist) {
    root.classList.toggle("is-cinema", on);
    document.documentElement.classList.toggle("lw-cinema-lock", on);
    el.backdrop.hidden = !on;
    el.cinema.setAttribute("aria-pressed", on ? "true" : "false");
    if (persist !== false) LS.set("learn:watch:cinema", on);
    if (on) { setView("player"); closeSheet(); }
  }
  el.cinema.addEventListener("click", function () { setCinema(!root.classList.contains("is-cinema")); });
  el.backdrop.addEventListener("click", function () { setCinema(false); });
  el.stage.addEventListener("click", function (e) { if (root.classList.contains("is-cinema") && (e.target === el.stage || e.target === el.player)) setCinema(false); });

  /* ---------- bottom sheets (mobile): comments + share ---------- */
  var sheetOpen = null;
  function sheetEl(name) { return root.querySelector('[data-sheet="' + name + '"]'); }
  // The one category row: desktop = the middle of the top bar, before the view toggle (17 Oct 2026);
  // mobile = under the player (player view) or above the grid (grid view).
  function placePlaylists() {
    var pl = el.playlists; if (!pl) return;
    var want = !isMobile() ? null : (state.view === "grid" ? el.gridHead : el.qhead);
    if (want) { if (pl.parentNode !== want) want.insertBefore(pl, want.firstChild); }
    else if (el.top && pl.parentNode !== el.top) el.top.insertBefore(pl, el.topRight && el.topRight.parentNode === el.top ? el.topRight : null);
  }
  function placeMobile(on) {
    placePlaylists();
    // Move the comments pane + form into the sheet (and back) so there is one DOM for both layouts. The share row lives in the share sheet on every width.
    var cs = sheetEl("comments"), ss = sheetEl("share");
    if (!cs || !ss) return;
    if (on) {
      if (el.commentsPane && el.commentsPane.parentNode !== $("[data-sheet-body]", cs)) $("[data-sheet-body]", cs).appendChild(el.commentsPane);
      if (el.formHost && el.formHost.parentNode !== $("[data-sheet-foot]", cs)) $("[data-sheet-foot]", cs).appendChild(el.formHost);
      el.commentsPane.hidden = false; el.formHost.hidden = false;
    } else {
      closeSheet();
      var scroll = el.panelScroll, panel = $(".lw-panel");
      if (el.commentsPane && el.commentsPane.parentNode !== scroll) { var vp = $('[data-tab-pane="videos"]', scroll); scroll.insertBefore(el.commentsPane, vp); }
      if (el.formHost && el.formHost.parentNode !== panel) panel.appendChild(el.formHost);
      var cur = $$("[data-tab-btn]").filter(function (b) { return b.getAttribute("aria-selected") === "true"; })[0];
      setTab(cur ? cur.getAttribute("data-tab-btn") : "videos");
    }
  }
  function openSheet(name) {
    var sh = sheetEl(name); if (!sh) return;
    closeSheet();
    sh.hidden = false; el.sheetBackdrop.hidden = false;
    document.documentElement.classList.add("lw-sheet-lock"); document.body.classList.add("learn-sheet-open");
    requestAnimationFrame(function () { sh.classList.add("is-open"); });
    sheetOpen = name;
    if (name === "comments") { var ta = $("textarea", sh); if (ta && (sh.getAttribute("data-focus") === "1")) ta.focus(); }
  }
  function closeSheet() {
    if (!sheetOpen) return;
    var sh = sheetEl(sheetOpen); sheetOpen = null;
    document.documentElement.classList.remove("lw-sheet-lock"); document.body.classList.remove("learn-sheet-open");
    el.sheetBackdrop.hidden = true;
    if (!sh) return;
    sh.classList.remove("is-open"); sh.style.transform = "";
    setTimeout(function () { if (!sh.classList.contains("is-open")) sh.hidden = true; }, 280);
  }
  $$("[data-sheet-close]").forEach(function (b) { b.addEventListener("click", closeSheet); });
  if (el.sheetBackdrop) el.sheetBackdrop.addEventListener("click", closeSheet);
  $$("[data-sheet-handle]").forEach(function (h) {
    var sh = h.closest("[data-sheet]"), y0 = null;
    h.addEventListener("touchstart", function (e) { y0 = e.touches[0].clientY; sh.style.transition = "none"; }, { passive: true });
    h.addEventListener("touchmove", function (e) { if (y0 == null) return; var dy = Math.max(0, e.touches[0].clientY - y0); sh.style.transform = "translateY(" + dy + "px)"; }, { passive: true });
    h.addEventListener("touchend", function (e) { if (y0 == null) return; var dy = (e.changedTouches[0].clientY - y0); sh.style.transition = ""; y0 = null; if (dy > 80) closeSheet(); else sh.style.transform = ""; });
    h.addEventListener("click", closeSheet);
  });
  $$("[data-rail-comments]", document).forEach(function (b) { b.addEventListener("click", function () { openSheet("comments"); }); });
  $$("[data-rail-share]", document).forEach(function (b) { b.addEventListener("click", function () { openSheet("share"); }); });
  // swipe up/down on the stage = next/prev (touches on the iframe itself stay with the player)
  (function () {
    var y0 = null, x0 = null;
    el.stage.addEventListener("touchstart", function (e) { y0 = e.touches[0].clientY; x0 = e.touches[0].clientX; }, { passive: true });
    el.stage.addEventListener("touchend", function (e) { if (y0 == null) return; var dy = e.changedTouches[0].clientY - y0, dx = e.changedTouches[0].clientX - x0; y0 = null; if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx) * 1.5) { if (dy < 0) go(1, true); else go(-1, true); } });
  })();
  placeMobile(isMobile());
  if (mqMobile.addEventListener) mqMobile.addEventListener("change", function (e) { placeMobile(e.matches); });
  else if (mqMobile.addListener) mqMobile.addListener(function (e) { placeMobile(e.matches); });

  /* ---------- keyboard ---------- */
  document.addEventListener("keydown", function (e) {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    var t = e.target, tag = (t && t.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select" || (t && t.isContentEditable)) return;
    switch (e.key) {
      case "n": case "N": go(1, true); e.preventDefault(); break;
      case "p": case "P": go(-1, true); e.preventDefault(); break;
      case "ArrowDown": case "ArrowUp": if (isMobile() || sheetOpen) break; if (t === document.body || t === el.stage || el.stage.contains(t)) { go(e.key === "ArrowDown" ? 1 : -1, true); e.preventDefault(); } break;
      case "l": case "L": toggleLike(); e.preventDefault(); break;
      case "m": case "M": setSound(state.muted); e.preventDefault(); break;
      case "c": case "C": setCinema(!root.classList.contains("is-cinema")); e.preventDefault(); break;
      case "Escape": if (sheetOpen) { closeSheet(); e.preventDefault(); } else if (root.classList.contains("is-cinema")) { setCinema(false); e.preventDefault(); } else if (state.countdown) stopCountdown(); break;
      case " ": if (el.stage.contains(document.activeElement) || document.activeElement === el.stage) { togglePlay(); e.preventDefault(); } break;
    }
  });

  /* ---------- mobile queue layout toggle (rail ↔ list) - sits right of the chip row ---------- */
  if (el.qhead) {
    var lay = document.createElement("button"); lay.type = "button"; lay.className = "lw-chip lw-chip--layout"; lay.textContent = stkT("stk.learn_watch.layout_list", "List"); lay.setAttribute("aria-pressed", "false");
    lay.addEventListener("click", function () { var on = el.queue.classList.toggle("is-list"); lay.setAttribute("aria-pressed", on ? "true" : "false"); lay.textContent = on ? stkT("stk.learn_watch.layout_rail", "Rail") : stkT("stk.learn_watch.layout_list", "List"); });
    el.qhead.appendChild(lay);
  }

  /* ---------- desktop: the player fills the first screen ----------
     17 Oct 2026 (Thomas: "why am i seeing the footer rather than making the viewing window bigger?").
     --lw-top = the page-y of the stage's top edge (site header + Learn header + category bar), measured
     from the real rect, so learn-watch.css sizes the stage to 100vh - --lw-top - a 24px foot (CSS
     fallback 330px). The stage's own height never moves its top, so this cannot feed back on itself.
     Skipped on a phone, in cinema and while the grid is showing (the stage is hidden there). */
  function measureStage() {
    if (isMobile() || root.classList.contains("is-cinema") || !el.viewPlayer || el.viewPlayer.hidden) return;
    var top = Math.round(el.stage.getBoundingClientRect().top + window.scrollY);
    if (top > 60 && top < 900) root.style.setProperty("--lw-top", top + "px");
  }
  var measureT; window.addEventListener("resize", function () { clearTimeout(measureT); measureT = setTimeout(measureStage, 120); });
  // the Learn header and the category bar can change height after first paint (fonts, cover images)
  if (window.ResizeObserver) { var stageRO = new ResizeObserver(function () { clearTimeout(measureT); measureT = setTimeout(measureStage, 60); }); [document.querySelector("[data-learn-nav]"), el.top].forEach(function (n) { if (n) stageRO.observe(n); }); }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureStage);

  /* ---------- boot ---------- */
  var booted = false;
  recompute();
  var wanted = null;
  try { wanted = new URL(location.href).searchParams.get("v"); } catch (e) { /* ignore */ }
  var fromLink = wanted;                                                       // a shared ?v= link always wins
  // P172 (2 Oct 2026): ?v= is the article id since 2 Oct; a handle of this language still resolves through byHandle.
  if (wanted && !byHandle[wanted] && byId[wanted]) wanted = byId[wanted].handle;
  if (!wanted && cfg.initial && byHandle[cfg.initial]) wanted = cfg.initial;   // article.learn-video: that article's video first
  // Rotate the opening video on the Watch page so a return visit does not always land on the
  // same one (Thomas, 2 Sep). Never on a video's own page, never over a shared ?v= link.
  // Non-embeddable items (Instagram, which has no oEmbed token) are skipped: opening on a
  // preview card that links off-site is a poor first frame. sessionStorage only remembers the
  // last pick so the same video does not come up twice in a row.
  if (!fromLink && cfg.rotate && !cfg.articleMode) {
    var pool = items.filter(function (it) { return it.embeddable !== false; });
    if (pool.length < 2) pool = items.slice();
    if (pool.length > 1) {
      var last = SS.get("learn:watch:last", "");
      var fresh = pool.filter(function (it) { return it.handle !== last; });
      if (fresh.length) pool = fresh;
    }
    if (pool.length) {
      var picked = pool[Math.floor(Math.random() * pool.length)].handle;
      SS.set("learn:watch:last", picked);
      wanted = picked;
    }
  }
  if (wanted && byHandle[wanted] && state.order.indexOf(wanted) === -1) { state.playlist = "all"; recompute(); }
  var first = (wanted && byHandle[wanted]) ? wanted : (state.order[0] || items[0].handle);
  var firstItem = byHandle[first];
  var tab = cfg.tab === "auto" ? ((firstItem.comments || 0) > 0 ? "comments" : "videos") : cfg.tab;
  setTab(tab);
  if (cfg.defaultView === "grid") setView("grid");
  select(first, { autoplay: cfg.autoplay });
  booted = true;
  if (LS.get("learn:watch:cinema", false) && window.innerWidth > 900) setCinema(true, false);
  root.classList.add("is-ready");
  // P172 (2 Oct 2026): links shared before 2 Oct carry the English handle, and /fr etc. now have their own, so
  // byHandle missed and the first video opened instead. Shopify 301s an article handle of any language to this
  // language's (measured: /fr/blogs/watch/<en> -> /fr/blogs/watch/<fr>, /blogs/watch/<fr> -> /blogs/watch/<en>),
  // so follow that redirect and open the video it lands on. Only a ?v= that resolved neither as a handle nor as an
  // id takes this path, so a link that already worked is untouched; a miss (404, a video past page 1) keeps the opener.
  if (fromLink && !byHandle[wanted] && !/^\d+$/.test(fromLink) && window.fetch) {
    var blogUrl = (root.getAttribute("data-blog-url") || "").replace(/\/$/, "");
    if (blogUrl) fetch(blogUrl + "/" + encodeURIComponent(fromLink), { method: "HEAD", credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok || state.cur !== first) return;   // the viewer already moved on
        var h = ""; try { h = decodeURIComponent(new URL(r.url, location.origin).pathname.split("/").pop() || ""); } catch (e) { return; }
        if (!byHandle[h] || h === state.cur) return;
        if (state.order.indexOf(h) === -1) { state.playlist = "all"; recompute(); }
        select(h, { autoplay: cfg.autoplay });
      })
      .catch(function () { /* offline: keep the opening video */ });
  }

  /* ---------- after posting a comment ----------------------------------------
     The comment form posts to the article and Shopify redirects to our
     return_to on the watch page, where Liquid's `form` object no longer exists.
     The form carries the outcome as ?posted=1 (live) or ?posted=pending
     (moderated), so read it here: open the comments pane or sheet for that
     video, put a notice at the top of the list, bring the new comment into
     view, then drop the flag from the URL so a refresh does not repeat it.
     Runs after select(), which rebuilds the comments host from the template. */
  (function () {
    var posted = null;
    try { posted = new URL(location.href).searchParams.get("posted"); } catch (e) { return; }
    if (!posted) return;

    if (el.commentsHost) {
      var old = $(".lw-cflash", el.commentsHost); if (old) old.parentNode.removeChild(old);
      var note = document.createElement("p");
      note.className = "lw-cflash";
      note.setAttribute("role", "status");
      note.textContent = posted === "pending" ? stkT("stk.learn_watch.comment_pending", "Thanks, your comment is awaiting approval.") : stkT("stk.learn_watch.comment_posted", "Thanks, your comment is up.");
      el.commentsHost.insertBefore(note, el.commentsHost.firstChild);
    }

    if (isMobile()) openSheet("comments"); else setTab("comments");

    // live comments land at the end of the list; a pending one is not rendered at all, so fall back to the notice
    var newest = posted === "pending" ? null : $$(".lw-clist .lw-c", el.commentsHost).pop();
    var target = newest || $(".lw-cflash", el.commentsHost);
    if (target) requestAnimationFrame(function () { target.scrollIntoView({ block: "center", behavior: "smooth" }); });

    try {
      var u = new URL(location.href);
      u.searchParams.delete("posted");
      history.replaceState(null, "", u.pathname + u.search + (u.hash || ""));
    } catch (e) { /* ignore */ }
  }());
  measureStage(); requestAnimationFrame(measureStage); window.addEventListener("load", measureStage);

  /* ---------- continuous scroll: append the next page instead of navigating ----------
   * Shopify's `paginate` caps a page at 50 articles and blog.articles cannot be walked past
   * it, so the library genuinely lives on several pages. Following the link would reload the
   * whole page and drop the visitor back into the player view, losing their place, so the
   * next page is fetched, its items merged into `items`/`byHandle` and its tiles cloned into
   * the queue and the grid. The <a rel="next"> stays in the DOM for crawlers and for no-JS.
   */
  var moreBox = $("[data-more]");
  if (moreBox) (function () {
    var moreLink = $("[data-more-link]", moreBox);
    var nextUrl = moreBox.getAttribute("data-next") || "";
    var busy = false, failed = 0;

    // Silent by design (Thomas, 2 Sep): no "loading" line and no "that's all N videos" line.
    // The grid simply grows and then stops. The only thing the visitor ever sees is the real
    // link, and only if loading has failed three times.
    if (moreLink) moreLink.hidden = true;

    function cloneInto(host, srcList, sentinelSel) {
      if (!host || !srcList) return;
      var stop = sentinelSel ? $(sentinelSel, host) : null;
      [].slice.call(srcList.children).forEach(function (node) {
        var h = node.getAttribute && node.getAttribute("data-watch-tile");
        if (!h || $('[data-watch-tile="' + h + '"]', host)) return;
        var copy = document.importNode(node, true);
        // 17 Oct 2026 round 5: the next page marks ITS first video current; only the playing one keeps the ring
        var on = h === state.cur, link = $("[data-tile-link]", copy);
        copy.classList.toggle("is-current", on);
        if (link) { if (on) link.setAttribute("aria-current", "true"); else link.removeAttribute("aria-current"); }
        $$("[data-views]", copy).forEach(function (v) { v.textContent = fmtN(v.getAttribute("data-views")); });
        if (stop) host.insertBefore(copy, stop); else host.appendChild(copy);
      });
    }

    function absorb(doc) {
      var blob = doc.querySelector("[data-watch-data]"), added = 0;
      if (blob) {
        var arr = [];
        try { arr = JSON.parse(blob.textContent) || []; } catch (e) { arr = []; }
        arr.forEach(function (it) {
          if (!it || byHandle[it.handle]) return;
          items.push(prep(it, items.length)); added++;
        });
      }
      cloneInto(el.queue, doc.querySelector("[data-queue]"), "[data-more-sentinel]");
      cloneInto(el.grid, doc.querySelector("[data-grid]"), "[data-more-sentinel]");
      // comment + form templates are looked up by handle off `document`, so they must land too
      [].slice.call(doc.querySelectorAll("template[data-watch-comments], template[data-watch-cform]")).forEach(function (t) {
        var k = t.hasAttribute("data-watch-comments") ? "data-watch-comments" : "data-watch-cform";
        var v = t.getAttribute(k);
        if (document.querySelector('template[' + k + '="' + (window.CSS && CSS.escape ? CSS.escape(v) : v) + '"]')) return;
        root.appendChild(document.importNode(t, true));
      });
      var nb = doc.querySelector("[data-more]");
      nextUrl = nb ? (nb.getAttribute("data-next") || "") : "";
      moreBox.setAttribute("data-next", nextUrl);   // keep the DOM honest for anything reading it
      if (moreLink) { if (nextUrl) moreLink.setAttribute("href", nextUrl); else moreLink.remove(); }
      return added;
    }

    function load() {
      if (busy || !nextUrl || failed >= 3) return;
      busy = true;
      var url = nextUrl;
      fetch(url, { credentials: "same-origin", headers: { "X-Requested-With": "fetch" } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var added = absorb(new DOMParser().parseFromString(html, "text/html"));
          busy = false; failed = 0;
          recompute();
          if (added && nextUrl) requestAnimationFrame(check);
        })
        .catch(function () {
          busy = false; failed++;
          if (failed >= 3 && moreLink) moreLink.hidden = false;   // give the real link back
        });
    }

    function check() {
      if (!nextUrl || busy) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var hit = $$("[data-more-sentinel]").some(function (n) {
        if (n.offsetParent === null && !n.getClientRects().length) return false;
        var r = n.getBoundingClientRect();
        return r.top < vh + 600 && r.bottom > -600;
      });
      if (hit) load();
    }

    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.isIntersecting; })) load();
      }, { rootMargin: "600px 0px" });
      $$("[data-more-sentinel]").forEach(function (n) { io.observe(n); });
    }
    // Belt and braces: the queue lives in its own scroller, and a filter can leave the
    // sentinel already on screen with no scroll event to follow.
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    // rAF does not run in a background tab, so a page restored from another tab (or opened
    // with the grid already short enough to show the sentinel) still gets a first check.
    document.addEventListener("visibilitychange", function () { if (!document.hidden) check(); });
    setTimeout(check, 300);
    window.addEventListener("load", check);
    if (el.panelScroll) el.panelScroll.addEventListener("scroll", check, { passive: true });
    if (el.viewGrid) el.viewGrid.addEventListener("scroll", check, { passive: true });
    $$("[data-view-btn]").forEach(function (b) { b.addEventListener("click", function () { setTimeout(check, 60); }); });
    if (el.playlists) el.playlists.addEventListener("click", function () { setTimeout(check, 60); });
    // and when the player is near the end of what we have, pull the next page in early
    root.addEventListener("lw:near-end", load);
    requestAnimationFrame(check);
  }());

  window.LearnWatch = { select: select, next: function () { go(1, true); }, prev: function () { go(-1, true); }, state: state, items: items, simulateEnded: onEnded, setCinema: setCinema, setView: setView, openSheet: openSheet, closeSheet: closeSheet, setSound: setSound };
})();
